import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, CircularProgress, Paper, Stack, IconButton, Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { getOnboardingKyc, uploadOnboardingKyc } from '../../api';
import CameraCapture, { dataUrlToFile } from '../common/CameraCapture';

const ACCENT = '#0e7c86';
const ACCENT_DARK = '#0a5a62';
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

const itemState = (item) => {
  if (item?.status === 'VERIFIED') return { label: 'Verified by HR', color: '#2e9e6b', bg: '#f3faf6' };
  if (item?.status === 'IN_REVIEW') return { label: 'Under HR review', color: '#0a5a62', bg: '#f2f9f9' };
  if (item?.status === 'RECEIVED' && item?.scan_status === 'CLEAN') {
    return { label: 'Received - awaiting HR review', color: '#0a5a62', bg: '#f2f9f9' };
  }
  if (item?.status === 'SCANNING') return { label: 'Security scan pending', color: '#b3801a', bg: '#fff8e8' };
  if (item?.status === 'REJECTED') return { label: 'Upload rejected', color: '#ce4b4b', bg: '#fff4f4' };
  if (item?.status === 'RECEIVED' && item?.scan_status !== 'CLEAN') {
    return { label: 'Security verification required', color: '#ce4b4b', bg: '#fff4f4' };
  }
  return { label: 'Ready to upload', color: '#64757d', bg: '#fff' };
};

// Passwordless KYC upload page. Employee token in the URL is the secret (same
// mechanism as /employee/form/). Loaded outside the app shell.
function tokenFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean); // [onboarding, upload, <token>]
  return parts[parts.length - 1];
}

export default function OnboardingUpload() {
  const token = tokenFromPath();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emp, setEmp] = useState(null);
  const [items, setItems] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const [cameraFor, setCameraFor] = useState(null); // {key, title} or null

  const load = async () => {
    try {
      const res = await getOnboardingKyc(token);
      setEmp(res.employee);
      setItems(res.kyc_items || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || 'This upload link is invalid.');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const hasScanningItems = items.some((item) => item.status === 'SCANNING');
  useEffect(() => {
    if (!hasScanningItems) return undefined;
    const timer = window.setInterval(load, 5000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, hasScanningItems]);

  const doUpload = async (docKey, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError('The document exceeds the 5 MB upload limit.');
      return;
    }
    if (!ALLOWED_FILE_TYPES.has(file.type)) {
      setError('Only PDF, JPG, JPEG, and PNG documents are supported.');
      return;
    }
    setBusyKey(docKey);
    setError(null);
    try {
      const res = await uploadOnboardingKyc(token, docKey, file);
      setItems((prev) => prev.map((it) => (it.doc_key === docKey ? res.kyc_item : it)));
    } catch (e) {
      setError(e?.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setBusyKey(null);
    }
  };

  const onCapture = (dataUrl) => {
    if (!cameraFor || !dataUrl) return;
    const file = dataUrlToFile(dataUrl, `${cameraFor.key}_photo.jpg`);
    doUpload(cameraFor.key, file);
  };

  const shell = (children) => (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef2f4', py: { xs: 2, md: 3.5 } }}>
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 3 } }}>
        <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: 1.2, background: `linear-gradient(150deg, ${ACCENT}, ${ACCENT_DARK})`,
            color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: 14 }}>ATS</Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#14232b', lineHeight: 1.1 }}>Aditya Trading Solutions</Typography>
            <Typography sx={{ fontSize: 11, color: '#7b96a2', letterSpacing: '.5px', textTransform: 'uppercase' }}>Document Upload</Typography>
          </Box>
        </Stack>
        {children}
      </Container>
    </Box>
  );

  if (loading) return shell(<Paper sx={{ p: 6, borderRadius: 3, textAlign: 'center' }}><CircularProgress sx={{ color: ACCENT }} /></Paper>);
  if (error && !emp) return shell(
    <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>Link unavailable</Typography>
      <Typography sx={{ color: '#64757d' }}>{error}</Typography>
    </Paper>
  );

  const receivedCount = items.filter((i) => i.status === 'VERIFIED').length;

  return shell(
    <Paper sx={{ p: { xs: 2, md: 3.5 }, borderRadius: 3, boxShadow: '0 4px 24px rgba(16,40,50,.08)' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 19, color: '#14232b' }}>Upload your documents</Typography>
          <Typography sx={{ color: '#64757d', fontSize: 13 }}>{emp?.name} · {emp?.employee_no}</Typography>
        </Box>
        <Typography sx={{ fontSize: 13, color: ACCENT_DARK, fontWeight: 600 }}>{receivedCount} / {items.length} verified</Typography>
      </Stack>

      {error && <Typography sx={{ color: '#ce4b4b', fontSize: 13, mb: 1.5 }}>{error}</Typography>}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(215px, 1fr))', gap: 1.25 }}>
        {items.map((it) => {
          const received = it.status === 'VERIFIED';
          const scanning = it.status === 'SCANNING';
          const canUpload = it.status === 'PENDING' || it.status === 'REJECTED';
          const busy = busyKey === it.doc_key;
          const state = itemState(it);
          return (
            <Box key={it.doc_key}
              sx={{ p: 1.25, border: '1px solid', borderColor: received ? '#bfe3cf' : '#e2e9ec',
                bgcolor: state.bg, borderRadius: 2,
                display: 'flex', flexDirection: 'column', gap: 0.9, minHeight: 84 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
                <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: '#14232b', lineHeight: 1.2 }}>
                  {it.title}{!it.required && <Box component="span" sx={{ color: '#9aa7ad', fontWeight: 400 }}> (optional)</Box>}
                </Typography>
                {received && <CheckCircleIcon sx={{ color: '#2e9e6b', fontSize: 18, flex: 'none' }} />}
              </Stack>
              <Typography sx={{ color: state.color, fontSize: 11.5, fontWeight: 600 }}>
                {state.label}
              </Typography>
              {it.status === 'REJECTED' && it.rejection_reason && (
                <Typography sx={{ color: '#8d4a4a', fontSize: 11 }}>{it.rejection_reason}</Typography>
              )}
              {canUpload && (
              <Stack direction="row" spacing={0.75} sx={{ mt: 'auto' }}>
                <Button component="label" size="small" variant={received ? 'outlined' : 'contained'} disabled={busy || scanning} fullWidth
                  startIcon={busy ? <CircularProgress size={13} sx={{ color: received ? ACCENT : '#fff' }} /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, py: 0.5, minWidth: 0,
                    ...(received ? { color: ACCENT_DARK, borderColor: '#cfe3e5' } : { bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_DARK } }) }}>
                  {scanning ? 'Scanning…' : (received ? 'Replace' : (it.status === 'REJECTED' ? 'Re-upload' : 'Upload'))}
                  <input hidden type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                    disabled={busy || scanning}
                    onChange={(e) => doUpload(it.doc_key, e.target.files?.[0])} />
                </Button>
                <Tooltip title="Take a photo">
                  <span>
                    <IconButton size="small" disabled={busy || scanning} onClick={() => setCameraFor({ key: it.doc_key, title: it.title })}
                      sx={{ border: '1px solid #cfe3e5', borderRadius: 1.5, color: ACCENT_DARK, p: 0.6 }}>
                      <CameraAltIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              )}
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ color: '#8a9099', fontSize: 11.5, mt: 2 }}>
        Accepted: PDF, JPG, JPEG, or PNG up to 5 MB. Files remain private and unavailable to HR until their security scan succeeds.
      </Typography>

      <CameraCapture open={!!cameraFor} title={cameraFor ? `Take a photo — ${cameraFor.title}` : 'Take a photo'}
        onClose={() => setCameraFor(null)} onCapture={onCapture} />
    </Paper>
  );
}
